import { componentFactoryOf } from 'vue-tsx-support';

import { AspectRatio, JtnUiImage, JtnUiRecordStatistics } from '@jtnews/jtn-ui';
import {
  injectStylesMixin,
  RecordItemUtilsMixin,
  ObserverVisibility
} from '@jtnews/shared';

import EntityDecoderMixin from '../../../../shared/mixins/entity-decoder.mixin';

import styles from './record-last-news-item.styles.scss?module';

type Image = {
  id: number;
  author: string;
  description: string;
  fileMask: string;
  height: number;
  sourceName: string;
  url: string;
  width: number;
};

type Format = {
  id: number;
  name: string;
  url: string;
};

type Theme = {
  id: number;
  name: string;
  path: string;
  url: string;
};

type Rubric = {
  id: string;
  name: string;
  path: string;
};

type LastNewsRecord = {
  id: number;
  block: 'block_commercial' | 'block_not_commercial';
  header: string;
  mainPhoto: Image | null;
  formats: Format[];
  themes: Theme[];
  rubrics: Rubric[];
  subheader: string;
  urls: {
    url: string;
    urlComments: string;
    urlCanonical: string;
  };
  viewsCount: number;
  commentsCount: number;
  isCommentsAllowed: boolean;
  publishAt: string;
  timeOrder: string;
  viewType: string;
  hasVideoMark?: boolean;
};

type ComponentData = {
  itemObsVisibility: ObserverVisibility;
};

interface IEvents {
  onRecordVisible: RecordLastNewsParams;
  onHeaderClicked: RecordLastNewsParams;
  onImageClicked: RecordLastNewsParams;
  onFormatClicked: RecordLastNewsEmitParams<NewsParams>;
  onRubricClicked: RecordLastNewsEmitParams<NewsParams>;
  onThemeClicked: RecordLastNewsEmitParams<NewsParams>;
  onDiscussClicked: RecordLastNewsEmitParams<Event>;
  onCommentsClicked: RecordLastNewsParams;
}
export type RecordLastNewsParams = {
  recordId: number;
  recordType: string;
  recordPlace: string;
};

export type NewsParams = { url: string; name: string };

export type RecordLastNewsEmitParams<T = void> = {
  recordParams: RecordLastNewsParams;
  value: T;
};

export default componentFactoryOf<IEvents>()
  .mixin(injectStylesMixin(styles))
  .mixin(EntityDecoderMixin)
  .mixin(RecordItemUtilsMixin)
  .create({
    name: 'RecordLastNewsItem',
    props: {
      record: {
        type: Object as () => LastNewsRecord,
        required: true
      },
      place: {
        type: Number,
        default: 0
      },
      isMobileOnly: {
        type: Boolean,
        default: false
      }
    },
    data(): ComponentData {
      return {
        itemObsVisibility: new ObserverVisibility('custom', {
          custom: {
            once: true,
            intersection: {
              rootMargin: '0px 0px -150px 0px',
              threshold: 0
            }
          }
        })
      };
    },
    computed: {
      recordType() {
        return this.record.block === 'block_commercial' ? 'Рекламный' : 'Редакционный';
      },
      recordParams(): RecordLastNewsParams {
        return {
          recordId: this.record.id,
          recordType: this.recordType,
          recordPlace: String(this.place)
        };
      }
    },
    mounted() {
      this.itemObsVisibility.event.subscribe((state: { isIntersecting: boolean }) => {
        if (state.isIntersecting) {
          this.onVisibleBlock();
        }
      });
    },
    beforeDestroy() {
      this.itemObsVisibility.destroy();
    },
    methods: {
      onVisibleBlock() {
        this.$emit('recordVisible', this.recordParams);
      },
      emitImageClick() {
        this.$emit('imageClicked', this.recordParams);
      },
      emitHeaderClick() {
        this.$emit('headerClicked', this.recordParams);
      },
      emitFormatClicked(url: string, name: string) {
        this.$emit('formatClicked', {
          recordParams: this.recordParams,
          value: { url, name }
        });
      },
      emitRubricClicked(url: string, name: string) {
        this.$emit('rubricClicked', {
          recordParams: this.recordParams,
          value: { url, name }
        });
      },
      emitThemeClicked(url: string, name: string) {
        this.$emit('themeClicked', {
          recordParams: this.recordParams,
          value: { url, name }
        });
      },
      emitDiscussClicked(event: Event) {
        this.$emit('discussClicked', {
          recordParams: this.recordParams,
          value: event
        });
      },
      emitCommentsClicked() {
        this.$emit('commentsClicked', this.recordParams);
      }
    },
    render() {
      return (
        <div
          v-observe-visibility={this.itemObsVisibility.getOptions('custom')}
          class={styles.record}
        >
          <a
            href={this.record.urls.url}
            class={styles.image}
            onClick={() => this.emitImageClick()}
          >
            <JtnUiImage
              src={this.record.mainPhoto?.data.img.src}
              sources={this.record.mainPhoto?.data.sources}
              hasCommercialLabel={this.record.mainPhoto?.data.hasCommercialLabel}
              loading={this.record.mainPhoto?.data.img.loading}
              decoding={this.record.mainPhoto?.data.img.decoding}
              placeholderImg={this.record.mainPhoto?.data.placeholderImg}
              aspectRatio={AspectRatio.Wide}
            />
          </a>
          <div class={styles.content}>
            <ul class={styles.linksList}>
              {this.record.rubrics.map(rubric => (
                <li class={styles.linksItem} key={rubric.id}>
                  <a
                    class={styles.link}
                    href={rubric.path}
                    onClick={() => this.emitRubricClicked(rubric.path, rubric.name)}
                  >
                    {rubric.name}
                  </a>
                </li>
              ))}
              {this.record.formats.map(format => (
                <li class={styles.linksItem} key={format.id}>
                  <a
                    class={styles.link}
                    href={format.url}
                    onClick={() => this.emitFormatClicked(format.url, format.name)}
                  >
                    {format.name}
                  </a>
                </li>
              ))}
              {this.record.themes.map(theme => (
                <li class={[styles.linksItem, styles.theme]} key={theme.id}>
                  <a
                    class={styles.link}
                    href={theme.path}
                    onClick={() => this.emitThemeClicked(theme.path, theme.name)}
                  >
                    {theme.name}
                  </a>
                </li>
              ))}
            </ul>
            <a
              class={styles.header}
              href={this.record.urls.url}
              onClick={() => this.emitHeaderClick()}
            >
              {this.entityDecode(this.record.header)}
            </a>
            <JtnUiRecordStatistics
              class={styles.statisticBlock}
              commentsCount={this.record.commentsCount}
              commentsUrl={this.record.urls.urlComments}
              discussUrl={this.record.urls.urlComments}
              isCommentsAllowed={this.record.isCommentsAllowed}
              viewsCount={this.record.viewsCount}
              onCommentsClicked={() => this.emitCommentsClicked()}
              onDiscussClicked={event => this.emitDiscussClicked(event)}
            />
          </div>
        </div>
      );
    }
  });
